import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Lese- und Schreibpfad des Ads-Steuerungs-Moduls, Scheibe 1.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WARUM HIER KEIN EINZIGER DIREKTER TABELLENZUGRIFF STEHT
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Die vier Tabellen des Moduls (`ads_rollout_accounts`, `ads_tier_runs`,
 * `ads_tier_run_items`, `ads_change_log`) und alle fünf RPCs sind
 * service-role-only. `authenticated` hat weder SELECT noch EXECUTE. Der einzige
 * Weg führt über die Edge Function `ads-tier-run`, die den Besitz selbst
 * prüft (`google_ads_accounts -> google_accounts.user_id = auth.uid()`).
 *
 * Ein `supabase.from('ads_tier_runs')` liefe hier also in ein `42501` und wäre
 * nicht bloß Stilbruch, sondern kaputt. Deshalb genau eine Zustellstelle:
 * `invokeAdsTierRun`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ZWEI PRÜFUNGEN, NICHT EINE
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Die Vorbedingungen der Schreibwege (Pilot zuerst, `write_enabled`, Ingest
 * jünger als 48 h, übernommener Lauf) stehen SERVERSEITIG in der Function und
 * in den RPCs. Die Oberfläche prüft sie trotzdem noch einmal selbst
 * (`tierApplyBlock`, `labelUploadBlock` weiter unten) -- nicht als Ersatz,
 * sondern als zweite Instanz:
 *
 *   - Ein Knopf, der erst beim Klick "nein" sagt, hat den Nutzer bereits in die
 *     Handlung geführt. Die Regel gehört in den Zustand des Knopfes.
 *   - Ein Server, der sich auf die ausgegraute Schaltfläche verlässt, ist
 *     über `curl` offen.
 *
 * Keine der beiden Seiten darf sich auf die andere verlassen. Die Fehlercodes
 * unten sind der Beweis, dass wir die Server-Antwort trotzdem ernst nehmen:
 * jeder erwartete 409 hat hier einen Namen.
 */

// ═════════════════════════════════════════════════════════════════════════════
// Typen des Vertrags (ads-steuerung-scheibe1-contract.md)
// ═════════════════════════════════════════════════════════════════════════════

export type AdsTier = 'hero' | 'mid' | 'drain' | 'zombie';
export const ADS_TIERS: readonly AdsTier[] = ['hero', 'mid', 'drain', 'zombie'];

/** `unchanged` kommt in `transitions` nie vor -- die Tafel zeigt Wanderung. */
export type AdsDamping = 'immediate' | 'confirmed' | 'pending' | 'unchanged';

/**
 * `computing` und `applying` sind die beiden Zwischenstände der
 * Teilschritt-Schleife (seit 23.08.): der Lauf wird gerade gerechnet bzw. gerade
 * übernommen. Sie MÜSSEN hier stehen, auch wenn die Fläche sie kaum je zeigt --
 * `parseRunHead` fällt bei einem unbekannten Wert auf einen Ersatzwert zurück,
 * und `dry_run` als Ersatz hiesse "übernehmbar". Ein halb übernommener Lauf
 * wäre damit als frischer Trockenlauf durchgegangen.
 */
export type AdsRunStatus = 'computing' | 'dry_run' | 'applying' | 'applied' | 'superseded';

export type AdsAccountRole = 'rollout' | 'negative_control';

/** Sperrgrund, den die Function AM KONTO meldet. */
export type AdsAccountBlockedReason =
  | 'ingest_stale'
  // Die ZWEITE Hälfte von G-D, und eigener Wert statt Abbildung auf
  // `ingest_stale`: die beiden Zustände verlangen verschiedene Handgriffe.
  // "Wir haben lange nicht gezogen" ist ein Problem bei uns, "das Konto
  // liefert seit Tagen nichts" eines beim Kunden -- wer das eine für das
  // andere hält, repariert den Ingest, während das Konto stillsteht.
  | 'data_blackout'
  | 'negative_control'
  | 'pilot_first'
  | 'no_applied_run'
  // Sperrfenster 22.09.-19.10. Fehlte hier zuerst, und das war nicht bloß ein
  // fehlender Typ: `oneOf` verwirft jeden Wert außerhalb der Liste zu `null`,
  // der Sperrgrund wäre also stillschweigend verschwunden und der Label-Knopf
  // wäre während des Messfensters bedienbar geblieben. Der Server hätte den
  // Klick abgelehnt -- aber ein Knopf, der erst beim Klick "nein" sagt, hat den
  // Nutzer bereits in die Handlung geführt.
  | 'logic_frozen'
  // Rotations-Moratorium 01.09.-13.09. Nur der Label-Upload ist gesperrt,
  // Rechnen und Übernehmen laufen weiter -- sonst gäbe es am 14.09. keine
  // bestätigte Dämpfungskette.
  | 'rotation_moratorium'
  // Auf einem Feed im Scope läuft ein A/B-Experiment. Der einzige Sperrgrund
  // dieser Liste, bei dem ein Schreibzugriff nicht nur verfrüht, sondern eine
  // fremde, laufende Messung zerstörend wäre.
  | 'experiment_running';

/** Sperrgrund, den die Function AM FEED meldet. */
export type AdsFeedBlockedReason =
  | 'ingest_stale'
  | 'no_free_slot'
  | 'no_tier'
  | 'mc_access'
  | 'nothing_eligible';

export interface AdsRunHead {
  id: string;
  status: AdsRunStatus;
  created_at: string | null;
  window_start: string | null;
  window_end: string | null;
  n_changes: number;
  n_immediate: number;
  n_confirmed: number;
  n_pending: number;
  /**
   * Wie viele Produktzeilen die Übernahme tatsächlich geschrieben hat, beim
   * Schreiben mitgeführt. Bei `status: 'applying'` eine laufende Zahl, sonst
   * das Endergebnis der letzten Übernahme (0, solange keine lief).
   *
   * Das ist die einzige Größe, an der ein auf halber Strecke stehengebliebener
   * Lauf seinen Fortschritt AUSWEIST. Vorher stand sie nur im Antwortkörper des
   * abgebrochenen Aufrufs und in der Log-Zeile, war nach einem Neuladen also
   * weg -- der Bediener sah einen Lauf mitten in der Übernahme, ohne zu
   * erfahren, wo die Strecke aufhört.
   *
   * `null` heißt "die Function hat das Feld nicht geliefert", NICHT "null
   * Produkte". Der Unterschied trägt: eine stille 0 an dieser Stelle würde
   * behaupten, es sei noch gar nichts geschrieben.
   */
  apply_updated: number | null;
  /** Nur im Trockenlauf-Kopf gesetzt, im `latest_run` der Übersicht nicht. */
  window_days: number | null;
  roas_target: number | null;
  data_through: string | null;
  ingest_age_hours: number | null;
  logic_version: string | null;
  thresholds: Record<string, unknown> | null;
  products_total: number | null;
  n_hero: number | null;
  n_mid: number | null;
  n_drain: number | null;
  n_zombie: number | null;
}

export interface AdsOverviewAccount {
  ads_account_id: string;
  ads_customer_id: string;
  label: string;
  role: AdsAccountRole;
  is_pilot: boolean;
  write_enabled: boolean;
  /** Es gibt eine Zeile in `google_ads_accounts`. Ohne sie kein Lauf. */
  connected: boolean;
  currency: string | null;
  data_through: string | null;
  ingest_age_hours: number | null;
  /** Älter als 48 h. Guardrail G-D: blind wird nie rotiert. */
  ingest_stale: boolean;
  /** Alter des jüngsten Ads-TAGES in Tagen. `null` = unbekannt. */
  data_age_days: number | null;
  /**
   * Zweite Hälfte von G-D: kommen überhaupt noch Tage dazu?
   *
   * `ingest_stale` misst unseren letzten Pull -- und der bleibt frisch, weil
   * er aus seinem 30-Tage-Rückblick dieselben alten Tage erneut schreibt. Ein
   * Konto, das seit Tagen nichts ausliefert, sieht daran also gesund aus.
   */
  data_blackout: boolean;
  /**
   * Wochenlauf (24.08.2026): Veto-Schalter und stehende Ankündigung. `paused`
   * stoppt nur die Upload-Phase (Rechnen/Übernehmen laufen weiter);
   * `upload_due_at` ist das Ende des 6h-Veto-Fensters, NULL = nichts
   * angekündigt.
   */
  rotation_paused: boolean;
  rotation_upload_due_at: string | null;
  feeds_in_scope: number;
  products_in_scope: number;
  /**
   * Serving-Gate (24.08.2026): davon bei Google für Shopping Ads abgelehnt.
   * Die Kennzeichnung, die verhindert, dass nicht-auslieferbare Produkte
   * unmarkiert in Auswertungen mitlaufen -- bei Spielgigant sind das 459 von
   * 3.777, davon 456 tote Landingpages.
   */
  products_disapproved: number;
  /** Abgeleiteter Ist-ROAS des Kontos. NIE gesetzt, nie editierbar (E3). */
  roas_target: number | null;
  tiers: Record<AdsTier, number>;
  latest_run: AdsRunHead | null;
  /** Nur bei Nicht-Piloten gesetzt. `null` = die Function sagt nichts dazu. */
  pilot_done: boolean | null;
  blocked_reason: AdsAccountBlockedReason | null;
}

export interface AdsPilotInfo {
  ads_customer_id: string;
  label: string;
  done: boolean;
}

export interface AdsFreezeWindow {
  active: boolean;
  from: string | null;
  to: string | null;
}

export interface AdsOverview {
  accounts: AdsOverviewAccount[];
  pilot: AdsPilotInfo | null;
  freeze: AdsFreezeWindow | null;
}

export interface AdsTransition {
  /** `null` = Produkt hatte bisher gar kein Tier. */
  from: AdsTier | null;
  to: AdsTier;
  n: number;
  damping: AdsDamping;
}

export interface AdsReasonCount {
  code: string;
  n: number;
}

export interface AdsDryRunResult {
  run: AdsRunHead;
  transitions: AdsTransition[];
  reasons: AdsReasonCount[];
  warnings: string[];
}

export interface AdsLabelFeed {
  feed_id: string;
  feed_name: string;
  mc_name: string | null;
  mc_access_status: string | null;
  /** Fünf Zähler, Slot 0 bis 4. Wert > 0 heißt: fremder Wert, Slot belegt. */
  used_slots: number[];
  /** Höchster freier Slot, `null` = keiner frei. */
  chosen_slot: number | null;
  products_total: number;
  with_tier: number;
  /** completed UND approved. NUR diese Produkte bekommen ein Label. */
  eligible: number;
  eligible_with_tier: number;
  /** Von Google abgelehnt: gehen mit hoch, aber ohne fo_perf-Label. */
  unlabeled_disapproved: number;
  /** Davon noch nie bei Google: der Lauf ist dort auch ein Content-Upload. */
  first_upload: number;
  awaiting_review: number;
  blocked_reason: AdsFeedBlockedReason | null;
}

export interface AdsLabelPreview {
  feeds: AdsLabelFeed[];
  account_blocked_reason: AdsAccountBlockedReason | null;
}

export type AdsChangeActor = 'software' | 'human';
export type AdsChangeClassification = 'gesperrt' | 'protokollpflichtig' | 'frei';

export interface AdsChangeLogEntry {
  id: string;
  occurred_at: string | null;
  ads_customer_id: string;
  account_label: string | null;
  feed_id: string | null;
  actor: AdsChangeActor;
  classification: AdsChangeClassification;
  action: string;
  summary: string;
  detail: Record<string, unknown> | null;
}

export interface AdsChangeLogInput {
  ads_customer_id: string;
  classification: AdsChangeClassification;
  /**
   * Die Eingriffsart, z.B. `budget_change` oder `troas_change`.
   *
   * Heißt `log_action` und nicht `action`, weil `action` im Rumpf schon die
   * Modul-Aktion trägt (`change_log_add`). Beides auf denselben Namen zu legen
   * war der erste Entwurf des Vertrags und ist genau einmal teuer geworden:
   * `{ action: 'change_log_add', ...variables }` hat die Routing-Aktion mit
   * dem überschrieben, was der Nutzer ins Formular tippt. Der Request wäre
   * auf eine unbekannte Aktion gelaufen, und der Handeintrag -- der einzige
   * Weg, auf dem Handarbeit in der Ads-Oberfläche ins Protokoll kommt --
   * hätte nie funktioniert. Ohne dieses Feld antwortet die Function mit
   * `400 LOG_ACTION_REQUIRED`.
   */
  log_action: string;
  summary: string;
  detail?: Record<string, unknown>;
  feed_id?: string;
  /** ISO-Zeitstempel. Fehlt er, setzt die RPC `now()`. */
  occurred_at?: string;
}

// ═════════════════════════════════════════════════════════════════════════════
// Fehler der Edge Function
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Ein benannter Fehler statt "Edge Function returned a non-2xx status code".
 *
 * `supabase-js` wirft bei JEDEM Non-2xx einen `FunctionsHttpError` mit genau
 * diesem pauschalen Satz und legt die rohe `Response` unter `.context` ab, ohne
 * sie zu lesen. Die Wahrheit steht im Body. Ohne den Helfer unten wären alle
 * sechs erwarteten 409er dieses Moduls für den Nutzer derselbe Satz.
 */
export class AdsSteuerungError extends Error {
  readonly code: string;
  readonly status: number | null;

  constructor(code: string, status: number | null = null) {
    super(code);
    this.name = 'AdsSteuerungError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Erkennung über `name`, nicht nur `instanceof`: letzteres bricht, sobald das
 * Modul in zwei Bundle-Kopien landet (dokumentiertes Muster aus
 * `_shared/experiment-control-guard.ts` und `lib/manageSubscription.ts`).
 */
export function isAdsSteuerungError(error: unknown): error is AdsSteuerungError {
  return (
    error instanceof AdsSteuerungError ||
    (error instanceof Error && error.name === 'AdsSteuerungError')
  );
}

/** Fehlercode aus einem beliebigen Fehler, `null` wenn es keiner von uns ist. */
export function adsErrorCode(error: unknown): string | null {
  if (!isAdsSteuerungError(error)) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/**
 * Retry-Politik der Lese-Queries dieses Moduls (H6-F3, E2E-Sweep 25.08.).
 *
 * Ein `AdsSteuerungError` mit 4xx-Status (403 ACCOUNT_NOT_AVAILABLE, die
 * benannten 409er) oder mit Fehlercode im 200er-Body ist ein VERDIKT der
 * Function -- dieselbe Antwort kaeme beim Wiederholen erneut. React Query
 * wiederholte den 403 der Label-Vorschau trotzdem zweimal (Projekt-Default
 * `retry: 2`): drei Konsolen-Errors und drei Requests fuer eine Antwort, die
 * sich nicht aendert. 5xx und nackte Netzwerkfehler bleiben Stoerungen und
 * behalten die zwei Wiederholungen. `status === null` heisst "Code ohne
 * lesbare Response" und wird als Verdikt behandelt -- die vorsichtige Annahme
 * wiederholt hier einen Request zu wenig statt zwei zu viel.
 */
export function adsQueryRetry(failureCount: number, error: unknown): boolean {
  if (isAdsSteuerungError(error)) {
    const status = (error as { status?: unknown }).status;
    if (typeof status !== 'number' || status < 500) return false;
  }
  return failureCount < 2;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Codefeld eines Fehler-Bodys. Der Vertrag schreibt `error`, `code` ist die
 *  in diesem Repo ebenfalls übliche Schreibweise (manage-subscription). */
function codeFromBody(body: unknown): string | null {
  if (!isRecord(body)) return null;
  if (typeof body.error === 'string' && body.error.length > 0) return body.error;
  if (typeof body.code === 'string' && body.code.length > 0) return body.code;
  return null;
}

async function throwFromInvokeError(error: unknown): Promise<never> {
  const context = (error as { context?: Response } | null)?.context;
  let body: unknown = null;

  if (context && typeof context.json === 'function' && !context.bodyUsed) {
    try {
      // Klonen, damit ein zweiter Leser den Body noch bekommt.
      const source = typeof context.clone === 'function' ? context.clone() : context;
      body = await source.json();
    } catch {
      // Body war kein JSON oder schon verbraucht: generisch weiter.
      body = null;
    }
  }

  const code = codeFromBody(body);
  if (code) throw new AdsSteuerungError(code, context?.status ?? null);
  throw error instanceof Error ? error : new Error(String(error));
}

/** Die einzige Zustellstelle des Moduls. */
async function invokeAdsTierRun<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('ads-tier-run', { body });
  if (error) await throwFromInvokeError(error);

  // Auch ein 200 kann einen Fehler tragen -- Muster aus `manageSubscription.ts`.
  const code = codeFromBody(data);
  if (code) throw new AdsSteuerungError(code, 200);
  if (data === null || data === undefined) throw new AdsSteuerungError('EMPTY_RESPONSE', 200);

  return data as T;
}

// ═════════════════════════════════════════════════════════════════════════════
// Normalisierung
// ═════════════════════════════════════════════════════════════════════════════
//
// PostgREST liefert `bigint` und `numeric` als STRING, und die Edge Function
// reicht die RPC-Zeilen durch. `formatInteger('3777')` wäre ein Strich, keine
// Zahl. Deshalb geht jede Zahl dieses Moduls durch `num`/`nullableNum` --
// dieselbe Haltung wie `toWireNumber` im Ads-Lesepfad.

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function nullableNum(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = num(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

function normalizeRun(raw: unknown): AdsRunHead | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  if (!id) return null;
  return {
    id,
    // Ersatzwert `superseded`, NICHT `dry_run`: ein unbekannter Status ist
    // etwas, das diese Fassung der Fläche nicht versteht, und die sichere
    // Lesart davon ist "nicht übernehmbar". Mit `dry_run` als Ersatz würde
    // jeder künftige Zustand automatisch als frischer Trockenlauf gelten und
    // `tierApplyBlock` liesse ihn durch.
    status: oneOf<AdsRunStatus>(raw.status, [
      'computing', 'dry_run', 'applying', 'applied', 'superseded',
    ]) ?? 'superseded',
    created_at: text(raw.created_at),
    window_start: text(raw.window_start),
    window_end: text(raw.window_end),
    n_changes: num(raw.n_changes),
    n_immediate: num(raw.n_immediate),
    n_confirmed: num(raw.n_confirmed),
    n_pending: num(raw.n_pending),
    // `nullableNum` statt `num` wie die Geschwister darueber, und das ist kein
    // Versehen: fehlt das Feld in der Antwort, waere eine stille 0 die
    // schlechtestmoegliche Auskunft -- sie behauptete, es sei noch nichts
    // geschrieben. `null` sagt stattdessen "unbekannt", und die Flaeche
    // verschweigt die Zahl dann, statt eine falsche zu zeigen.
    apply_updated: nullableNum(raw.apply_updated),
    window_days: nullableNum(raw.window_days),
    roas_target: nullableNum(raw.roas_target),
    data_through: text(raw.data_through),
    ingest_age_hours: nullableNum(raw.ingest_age_hours),
    logic_version: text(raw.logic_version),
    thresholds: isRecord(raw.thresholds) ? raw.thresholds : null,
    products_total: nullableNum(raw.products_total),
    n_hero: nullableNum(raw.n_hero),
    n_mid: nullableNum(raw.n_mid),
    n_drain: nullableNum(raw.n_drain),
    n_zombie: nullableNum(raw.n_zombie),
  };
}

function normalizeAccount(raw: unknown): AdsOverviewAccount | null {
  if (!isRecord(raw)) return null;
  const customerId = text(raw.ads_customer_id);
  if (!customerId) return null;
  const tiersRaw = isRecord(raw.tiers) ? raw.tiers : {};
  return {
    ads_account_id: text(raw.ads_account_id) ?? '',
    ads_customer_id: customerId,
    label: text(raw.label) ?? customerId,
    role: oneOf<AdsAccountRole>(raw.role, ['rollout', 'negative_control']) ?? 'rollout',
    is_pilot: bool(raw.is_pilot),
    write_enabled: bool(raw.write_enabled),
    // Fehlt das Feld, gilt das Konto als NICHT verbunden. Die vorsichtige
    // Annahme sperrt einen Knopf zu viel statt einen zu wenig.
    connected: bool(raw.connected),
    currency: text(raw.currency),
    data_through: text(raw.data_through),
    ingest_age_hours: nullableNum(raw.ingest_age_hours),
    ingest_stale: bool(raw.ingest_stale),
    data_age_days: nullableNum(raw.data_age_days),
    data_blackout: bool(raw.data_blackout),
    rotation_paused: bool(raw.rotation_paused),
    rotation_upload_due_at: text(raw.rotation_upload_due_at),
    feeds_in_scope: num(raw.feeds_in_scope),
    products_in_scope: num(raw.products_in_scope),
    products_disapproved: num(raw.products_disapproved),
    roas_target: nullableNum(raw.roas_target),
    tiers: {
      hero: num(tiersRaw.hero),
      mid: num(tiersRaw.mid),
      drain: num(tiersRaw.drain),
      zombie: num(tiersRaw.zombie),
    },
    latest_run: normalizeRun(raw.latest_run),
    pilot_done: typeof raw.pilot_done === 'boolean' ? raw.pilot_done : null,
    blocked_reason: oneOf<AdsAccountBlockedReason>(raw.blocked_reason, [
      'ingest_stale',
      'data_blackout',
      'negative_control',
      'pilot_first',
      'no_applied_run',
      // `experiment_running` meldet auch die Übersicht (Stufe 'overview'),
      // `logic_frozen` und `rotation_moratorium` dagegen nicht -- die beiden
      // gelten erst ab 'apply' bzw. 'label_upload'. Die Liste bildet ab, was
      // auf DIESER Stufe vorkommen kann; ein Wert daneben verschwände still.
      'experiment_running',
    ]),
  };
}

export function normalizeOverview(raw: unknown): AdsOverview {
  const record = isRecord(raw) ? raw : {};
  const accountsRaw = Array.isArray(record.accounts) ? record.accounts : [];
  const accounts = accountsRaw
    .map(normalizeAccount)
    .filter((account): account is AdsOverviewAccount => account !== null);

  const pilotRaw = isRecord(record.pilot) ? record.pilot : null;
  const pilot: AdsPilotInfo | null =
    pilotRaw && text(pilotRaw.ads_customer_id)
      ? {
          ads_customer_id: text(pilotRaw.ads_customer_id) as string,
          label: text(pilotRaw.label) ?? (text(pilotRaw.ads_customer_id) as string),
          done: bool(pilotRaw.done),
        }
      : null;

  const freezeRaw = isRecord(record.freeze) ? record.freeze : null;
  const freeze: AdsFreezeWindow | null = freezeRaw
    ? { active: bool(freezeRaw.active), from: text(freezeRaw.from), to: text(freezeRaw.to) }
    : null;

  return { accounts, pilot, freeze };
}

export function normalizeDryRun(raw: unknown): AdsDryRunResult | null {
  if (!isRecord(raw)) return null;
  const run = normalizeRun(raw.run);
  if (!run) return null;

  const transitionsRaw = Array.isArray(raw.transitions) ? raw.transitions : [];
  const transitions: AdsTransition[] = transitionsRaw
    .map((entry): AdsTransition | null => {
      if (!isRecord(entry)) return null;
      const to = oneOf<AdsTier>(entry.to, ADS_TIERS);
      if (!to) return null;
      return {
        from: oneOf<AdsTier>(entry.from, ADS_TIERS),
        to,
        n: num(entry.n),
        damping:
          oneOf<AdsDamping>(entry.damping, ['immediate', 'confirmed', 'pending', 'unchanged']) ??
          'pending',
      };
    })
    .filter((entry): entry is AdsTransition => entry !== null);

  const reasonsRaw = Array.isArray(raw.reasons) ? raw.reasons : [];
  const reasons: AdsReasonCount[] = reasonsRaw
    .map((entry): AdsReasonCount | null => {
      if (!isRecord(entry)) return null;
      const code = text(entry.code);
      return code ? { code, n: num(entry.n) } : null;
    })
    .filter((entry): entry is AdsReasonCount => entry !== null);

  const warningsRaw = Array.isArray(raw.warnings) ? raw.warnings : [];
  const warnings = warningsRaw.filter((entry): entry is string => typeof entry === 'string');

  return { run, transitions, reasons, warnings };
}

function normalizeLabelFeed(raw: unknown): AdsLabelFeed | null {
  if (!isRecord(raw)) return null;
  const feedId = text(raw.feed_id);
  if (!feedId) return null;
  const slotsRaw = Array.isArray(raw.used_slots) ? raw.used_slots : [];
  return {
    feed_id: feedId,
    feed_name: text(raw.feed_name) ?? feedId,
    mc_name: text(raw.mc_name),
    mc_access_status: text(raw.mc_access_status),
    used_slots: [0, 1, 2, 3, 4].map((index) => num(slotsRaw[index])),
    chosen_slot: nullableNum(raw.chosen_slot),
    products_total: num(raw.products_total),
    with_tier: num(raw.with_tier),
    eligible: num(raw.eligible),
    eligible_with_tier: num(raw.eligible_with_tier),
    unlabeled_disapproved: num(raw.unlabeled_disapproved),
    first_upload: num(raw.first_upload),
    awaiting_review: num(raw.awaiting_review),
    blocked_reason: oneOf<AdsFeedBlockedReason>(raw.blocked_reason, [
      'ingest_stale',
      'no_free_slot',
      'no_tier',
      'mc_access',
      'nothing_eligible',
    ]),
  };
}

export function normalizeLabelPreview(raw: unknown): AdsLabelPreview {
  const record = isRecord(raw) ? raw : {};
  const feedsRaw = Array.isArray(record.feeds) ? record.feeds : [];
  return {
    feeds: feedsRaw
      .map(normalizeLabelFeed)
      .filter((feed): feed is AdsLabelFeed => feed !== null),
    account_blocked_reason: oneOf<AdsAccountBlockedReason>(record.account_blocked_reason, [
      'ingest_stale',
      'data_blackout',
      'negative_control',
      'pilot_first',
      'no_applied_run',
      'logic_frozen',
      'rotation_moratorium',
      'experiment_running',
    ]),
  };
}

/**
 * Der Vertrag sagt zur Antwortform des Änderungs-Logs nur "Liste". Deshalb
 * werden beide plausiblen Formen gelesen: das nackte Array und der Umschlag
 * `{ entries: [...] }`. Eine Fehlannahme darf hier keine leere Tafel erzeugen,
 * die wie "es gab keine Eingriffe" aussieht -- das ist die eine Aussage, die
 * dieses Protokoll niemals fälschlich machen darf.
 */
export function normalizeChangeLog(raw: unknown): AdsChangeLogEntry[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : isRecord(raw) && Array.isArray(raw.entries)
      ? raw.entries
      : isRecord(raw) && Array.isArray(raw.log)
        ? raw.log
        : [];

  return list
    .map((entry): AdsChangeLogEntry | null => {
      if (!isRecord(entry)) return null;
      const id = text(entry.id);
      if (!id) return null;
      return {
        id,
        occurred_at: text(entry.occurred_at),
        ads_customer_id: text(entry.ads_customer_id) ?? '',
        account_label: text(entry.account_label),
        feed_id: text(entry.feed_id),
        actor: oneOf<AdsChangeActor>(entry.actor, ['software', 'human']) ?? 'software',
        classification:
          oneOf<AdsChangeClassification>(entry.classification, [
            'gesperrt',
            'protokollpflichtig',
            'frei',
          ]) ?? 'protokollpflichtig',
        action: text(entry.action) ?? '',
        summary: text(entry.summary) ?? '',
        detail: isRecord(entry.detail) ? entry.detail : null,
      };
    })
    .filter((entry): entry is AdsChangeLogEntry => entry !== null);
}

// ═════════════════════════════════════════════════════════════════════════════
// Der letzte Label-Lauf eines Feeds
// ═════════════════════════════════════════════════════════════════════════════
//
// WARUM DAS ÄNDERUNGS-LOG DIE QUELLE IST UND NICHT DIE LABEL-VORSCHAU
//
// `ads_label_upload_preview` rechnet `used_slots` aus `products.custom_label_*`.
// Diese Spalte füllt allein der nächtliche Sync aus Googles Echo, nie der
// Upload -- und selbst dann zählen unsere eigenen `fo_%`-Werte dort bewusst
// NICHT als belegter Slot. Die Vorschau liefert nach einem Label-Lauf also
// Zeichen für Zeichen dieselben Werte wie davor (live nachgemessen). Der
// einzige bleibende Beleg, dass etwas das Haus verlassen hat, ist die Zeile
// `label_upload` im Änderungs-Log.

/** `ads_change_log.action`, das die Software für einen Label-Lauf schreibt. */
export const LABEL_UPLOAD_ACTION = 'label_upload';

/**
 * Wie tief das Änderungs-Log geladen wird -- und damit zugleich die Tiefe, aus
 * der die Label-Karte ihren letzten Lauf liest.
 *
 * Es ist bewusst DIESELBE Abfrage wie für die Protokolltafel und nicht eine
 * zweite, tiefere: die Seite fragt genau drei Lese-Aktionen ab, und ein
 * vierter Request bei jedem Seitenaufruf wäre für eine Angabe, die heute aus
 * vier Log-Zeilen je Konto zu holen ist, nicht zu rechtfertigen
 * (`AdsSteuerung.smoke.test.tsx` hält das fest).
 *
 * Die Tiefe trägt hier trotzdem mehr als bei der Tafel: dort ist eine
 * abgeschnittene Liste bloß eine kürzere Liste, hier entscheidet sie darüber,
 * ob "noch nie gelabelt" überhaupt gesagt werden darf. Deshalb prüft
 * `lastLabelRunFor` gegen genau diesen Wert und weicht auf "unbekannt" aus,
 * statt zu raten. Wächst das Protokoll so weit, dass die Angabe regelmäßig
 * verschwindet, ist der Ausweg eine zweite, kontogescopte Abfrage mit
 * höherem Limit -- dann aber als bewusste Entscheidung samt Testanpassung.
 */
export const LABEL_HISTORY_LIMIT = 50;

/** Was ein Label-Lauf hinterlassen hat, gelesen aus seiner Log-Zeile. */
export interface AdsLastLabelRun {
  occurred_at: string | null;
  /**
   * Produkte, die bei diesem Lauf ein Label bekommen haben, also
   * `eligible_with_tier` und nicht `eligible`: der Upload umfasst alle
   * freigegebenen Produkte, ein Label bekommt davon nur, wer ein gerechnetes
   * Tier trägt. Es ist zugleich die Zahl, die die Spalte "labelbar" derselben
   * Zeile zeigt -- zwei verschiedene Größen nebeneinander wären an dieser
   * Stelle nicht als solche zu erkennen.
   */
  eligible_with_tier: number | null;
  chosen_slot: number | null;
}

/**
 * Drei Zustände, nicht zwei.
 *
 * "noch nie gelabelt" ist eine Aussage über den Feed, `unknown` eine über uns.
 * Fielen beide zusammen, stünde nach jedem Seitenaufruf für einen Wimpernschlag
 * "noch nie gelabelt" an einem Feed, der gestern gelabelt wurde -- und nach
 * einer fehlgeschlagenen Abfrage dauerhaft.
 */
export type AdsLastLabelRunState =
  | { kind: 'unknown' }
  | { kind: 'never' }
  | { kind: 'known'; run: AdsLastLabelRun };

/** `occurred_at` als Zahl. Ohne Zeitstempel gewinnt eine Zeile keinen Vergleich. */
function occurredAtMs(entry: AdsChangeLogEntry): number {
  const parsed = entry.occurred_at ? Date.parse(entry.occurred_at) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

/** Der Feed einer Log-Zeile. Die Spalte führt, das Detail ist der Rückfall. */
function feedOfEntry(entry: AdsChangeLogEntry): string | null {
  return entry.feed_id ?? text(entry.detail?.feed_id);
}

export function lastLabelRunFor(
  entries: AdsChangeLogEntry[] | undefined,
  feedId: string,
  /** MUSS zum Limit der Abfrage passen, aus der `entries` stammt. */
  loadedLimit: number = LABEL_HISTORY_LIMIT,
): AdsLastLabelRunState {
  // `undefined` deckt beides ab: noch am Laden und fehlgeschlagen. Beides ist
  // Unwissen, und Unwissen sagt über den Feed nichts.
  if (!entries) return { kind: 'unknown' };

  let newest: AdsChangeLogEntry | null = null;
  for (const entry of entries) {
    if (entry.action !== LABEL_UPLOAD_ACTION) continue;
    if (feedOfEntry(entry) !== feedId) continue;
    // Die Antwort kommt heute nach `occurred_at DESC` sortiert. Sich darauf zu
    // verlassen hiesse, eine Eigenschaft der Function zur Voraussetzung dieser
    // Anzeige zu machen, ohne dass es irgendwo stünde.
    if (newest === null || occurredAtMs(entry) > occurredAtMs(newest)) newest = entry;
  }

  if (newest) {
    const detail = newest.detail ?? {};
    return {
      kind: 'known',
      run: {
        occurred_at: newest.occurred_at,
        eligible_with_tier: nullableNum(detail.eligible_with_tier),
        chosen_slot: nullableNum(detail.chosen_slot),
      },
    };
  }

  // Kein Treffer heisst nur dann "noch nie gelabelt", wenn das ganze Protokoll
  // dieses Kontos vorlag. Kam es am Limit zurück, kann ein älterer Lauf
  // abgeschnitten sein, und "nie" wäre eine Behauptung über Zeilen, die nie
  // geladen wurden.
  return entries.length >= loadedLimit ? { kind: 'unknown' } : { kind: 'never' };
}

// ═════════════════════════════════════════════════════════════════════════════
// Guardrail-Befunde (`action: "guardrails"`)
// ═════════════════════════════════════════════════════════════════════════════

export type AdsGuardrailSeverity = 'unknown' | 'info' | 'warn' | 'breach';

export interface AdsGuardrailCheck {
  ads_customer_id: string;
  account_label: string | null;
  /**
   * BEWUSST `string` und kein Aufzählungstyp.
   *
   * Die Spalte `ads_guardrail_checks.guardrail` trägt absichtlich keinen
   * CHECK-Constraint: kommt eine fünfte Gültigkeitsbedingung dazu, darf ihr
   * Befund nicht am Constraint scheitern. Ein `oneOf` hier wäre dieselbe
   * Enge eine Schicht höher -- der neue Guardrail käme an, würde zu `null`
   * normalisiert und verschwände lautlos aus der Tafel. Die Fläche rendert
   * deshalb, was sie bekommt, und kennt nur für G-A bis G-D eine Überschrift.
   */
  guardrail: string;
  severity: AdsGuardrailSeverity;
  value: number | null;
  value_label: string | null;
  message: string | null;
  detail: Record<string, unknown>;
  checked_at: string | null;
  check_day: string | null;
}

export interface AdsGuardrailReport {
  checks: AdsGuardrailCheck[];
  /** false = noch kein Lauf. Ausdrücklich NICHT dasselbe wie "alles ruhig". */
  has_history: boolean;
}

function normalizeGuardrailCheck(raw: unknown): AdsGuardrailCheck | null {
  if (!isRecord(raw)) return null;
  const guardrail = typeof raw.guardrail === 'string' ? raw.guardrail : '';
  if (guardrail === '') return null;
  return {
    ads_customer_id: String(raw.ads_customer_id ?? ''),
    account_label: typeof raw.account_label === 'string' ? raw.account_label : null,
    guardrail,
    // Ersatzwert `unknown`, nicht `info`: ein unbekannter Schweregrad ist ein
    // ungeprüfter, und ein ungeprüfter darf nicht wie "geprüft und in Ordnung"
    // aussehen. Dieselbe Trennung trägt die Spalte selbst.
    severity: oneOf<AdsGuardrailSeverity>(raw.severity, ['unknown', 'info', 'warn', 'breach'])
      ?? 'unknown',
    value: nullableNum(raw.value),
    value_label: typeof raw.value_label === 'string' ? raw.value_label : null,
    message: typeof raw.message === 'string' ? raw.message : null,
    detail: isRecord(raw.detail) ? raw.detail : {},
    checked_at: typeof raw.checked_at === 'string' ? raw.checked_at : null,
    check_day: typeof raw.check_day === 'string' ? raw.check_day : null,
  };
}

export function normalizeGuardrails(raw: unknown): AdsGuardrailReport {
  const record = isRecord(raw) ? raw : {};
  const list = Array.isArray(record.checks) ? record.checks : [];
  const checks = list
    .map(normalizeGuardrailCheck)
    .filter((check): check is AdsGuardrailCheck => check !== null);
  return {
    checks,
    // Nicht aus `checks.length` abgeleitet: die Function beantwortet die Frage
    // selbst, und "0 Befunde" kann auch heissen, dass der Aufrufer kein Konto
    // sieht. Der Unterschied gehört auf die Fläche.
    has_history: typeof record.has_history === 'boolean'
      ? record.has_history
      : checks.length > 0,
  };
}

/** Die Wochenreihe von G-A, wie der tägliche Lauf sie in `detail` ablegt. */
export interface AdsGuardrailWeek {
  week_start: string;
  iso_week: number | null;
  clicks: number | null;
  pct_of_pre: number | null;
  is_pre: boolean;
  /** Tage der Woche MIT Ingest-Zeile. Reine Diagnose. */
  days_with_data: number | null;
  /**
   * Tage der Woche, die im ingestierten Fenster des Kontos liegen - und damit
   * die Zahl, an der die Bewertbarkeit haengt.
   *
   * Der Unterschied ist nicht kosmetisch: `shopping_performance_view` liefert
   * fuer einen Tag OHNE Auslieferung gar keine Zeile. Eine vollstaendig dunkle
   * Woche hat deshalb `days_with_data = 0` und trotzdem `days_measured = 7` -
   * sie ist gemessen, und sie ist ein Verstoss. Wer hier auf days_with_data
   * schaut, zeichnet genau diesen Fall als Datenloch.
   */
  days_measured: number | null;
}

export function guardrailWeeks(check: AdsGuardrailCheck | null): AdsGuardrailWeek[] {
  const raw = check?.detail?.weeks;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!isRecord(entry) || typeof entry.week_start !== 'string') return [];
    return [{
      week_start: entry.week_start,
      iso_week: nullableNum(entry.iso_week),
      clicks: nullableNum(entry.clicks),
      pct_of_pre: nullableNum(entry.pct_of_pre),
      is_pre: entry.is_pre === true,
      days_with_data: nullableNum(entry.days_with_data),
      days_measured: nullableNum(entry.days_measured),
    }];
  });
}

/**
 * Die Wochenreihe von G-E, wie der Watchdog sie in `detail.weeks` ablegt.
 *
 * Anders als G-A trägt G-E in `detail` KEIN rohes Ergebnis der RPC --
 * `ads_guardrail_g_e` liefert nur Rohreihen (Summen, Tageszählungen), das
 * Verdikt inklusive `pct_of_pre` rechnet der Watchdog. `control_pct` und
 * `prior_year_pct` sind Kontext, keine Umdeutung (Spec-Entscheidung 5): sie
 * stehen ausschließlich zur Diagnose neben dem eigenen Wert, nie als
 * Ersatzmaßstab. `null` heißt in beiden Fällen "nicht messbar", niemals
 * "null Prozent" -- bei golfimport selbst ist `control_pct` immer `null`.
 */
export interface AdsGuardrailGeWeek {
  week_start: string;
  iso_week: number | null;
  is_pre: boolean;
  pct_of_pre: number | null;
  days_measured: number | null;
  /** Erst ab 3 Tagen Ausreifung ist eine Woche überhaupt bewertbar (Nachattribution). */
  settled: boolean;
  /** `false` bei einer Pre-Woche oder einer noch nicht ausgereiften Messwoche. */
  scored: boolean;
  below_threshold: boolean;
  control_pct: number | null;
  prior_year_pct: number | null;
}

export function guardrailGeWeeks(check: AdsGuardrailCheck | null): AdsGuardrailGeWeek[] {
  const raw = check?.detail?.weeks;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!isRecord(entry) || typeof entry.week_start !== 'string') return [];
    return [{
      week_start: entry.week_start,
      iso_week: nullableNum(entry.iso_week),
      is_pre: entry.is_pre === true,
      pct_of_pre: nullableNum(entry.pct_of_pre),
      days_measured: nullableNum(entry.days_measured),
      settled: entry.settled === true,
      scored: entry.scored === true,
      below_threshold: entry.below_threshold === true,
      control_pct: nullableNum(entry.control_pct),
      prior_year_pct: nullableNum(entry.prior_year_pct),
    }];
  });
}

/**
 * Die Schwelle, gegen die G-E seine Wochen zeichnet.
 *
 * `detail.threshold_pct` ist Teil des Befunds -- die Tafel darf sie nicht neu
 * annehmen, sondern liest sie mit, und fällt nur ohne Befund auf den
 * dokumentierten Standardwert (60) zurück.
 */
export function guardrailGeThresholdPct(check: AdsGuardrailCheck | null): number {
  return nullableNum(check?.detail?.threshold_pct) ?? 60;
}

/**
 * `true`, wenn dieses Konto laut Befund selbst die Kontrolle ist (golfimport).
 *
 * `detail.control` trägt in diesem Fall `{is_self: true}` statt der
 * Kundenkonto-Form `{ads_customer_id, label, frozen, weekly_mean}` -- und für
 * jedes andere Konto entweder diese Form oder `null`, wenn die Kontrolle für
 * dieselbe Woche nicht gewertet ist.
 */
export function guardrailGeIsSelfControl(check: AdsGuardrailCheck | null): boolean {
  const control = check?.detail?.control;
  return isRecord(control) && control.is_self === true;
}

/**
 * Ein Punkt der G-T-Reihe (Treffsicherheit, Ads-Ausbau 0.8), wie der Watchdog
 * ihn in `detail.series.<variante>` ablegt. `lead_pct` null heisst
 * "unbestimmt" (Mindestmenge verfehlt), nie "null Prozent". Keine Summen: die
 * Tafel zeigt Vorsprung, Intervall und Produktzahlen (Blindheit, Spec 0.8
 * Abschnitt 5).
 *
 * Bewusst genau diese sechs Felder und kein Spread des Rohpunkts: stuende je
 * ein Kosten- oder Umsatzfeld im Detail, darf es die Flaeche trotzdem nicht
 * erreichen. Die Zahlen gehen durch `nullableNum`, weil PostgREST `numeric`
 * als String liefert und die Intervallgrenzen dort rund hundert
 * Nachkommastellen tragen.
 */
export interface AdsGuardrailGtPoint {
  d0: string;
  lead_pct: number | null;
  lead_ci_low: number | null;
  lead_ci_high: number | null;
  n_in_a: number | null;
  n_not_a: number | null;
}

export function guardrailGtSeries(
  check: AdsGuardrailCheck | null,
  variant: 'v1' | 'v2',
): AdsGuardrailGtPoint[] {
  const series = check?.detail?.series;
  const raw = isRecord(series) ? series[variant] : null;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!isRecord(entry) || typeof entry.d0 !== 'string') return [];
    return [{
      d0: entry.d0,
      lead_pct: nullableNum(entry.lead_pct),
      lead_ci_low: nullableNum(entry.lead_ci_low),
      lead_ci_high: nullableNum(entry.lead_ci_high),
      n_in_a: nullableNum(entry.n_in_a),
      n_not_a: nullableNum(entry.n_not_a),
    }];
  });
}

// ═════════════════════════════════════════════════════════════════════════════
// Sperrgründe der Oberfläche (zweite Instanz, siehe Kopf)
// ═════════════════════════════════════════════════════════════════════════════

/** Warum "Übernehmen" nicht bedienbar ist. `null` = bedienbar. */
export type TierApplyBlock =
  | 'no_account'
  | 'negative_control'
  | 'write_disabled'
  | 'not_connected'
  | 'pilot_first'
  | 'no_dry_run'
  | 'run_already_applied'
  | 'dry_run_expired'
  | 'experiment_running';

/** Warum ein Label-Lauf nicht bedienbar ist. `null` = bedienbar. */
export type LabelUploadBlock =
  | 'no_account'
  | 'negative_control'
  | 'write_disabled'
  | 'not_connected'
  | 'pilot_first'
  | 'ingest_stale'
  | 'data_blackout'
  | 'no_applied_run'
  | 'no_preview'
  // Sperrfenster 22.09.-19.10. Kommt ausschließlich vom Server (die
  // Oberfläche kennt die übernommene logic_version nicht) und wird deshalb
  // hier nur durchgereicht.
  | 'logic_frozen'
  | 'rotation_moratorium'
  | 'experiment_running'
  | AdsFeedBlockedReason;

/** Lebensdauer eines Trockenlaufs, gespiegelt aus `ads_tier_apply_begin`. */
export const DRY_RUN_MAX_AGE_HOURS = 24;

export function runAgeHours(run: AdsRunHead | null, now: number = Date.now()): number | null {
  if (!run?.created_at) return null;
  const created = Date.parse(run.created_at);
  if (!Number.isFinite(created)) return null;
  return (now - created) / 3_600_000;
}

/**
 * Ist der Pilot durch? Die Function meldet das an zwei Stellen (`pilot.done`
 * global, `pilot_done` je Nicht-Pilot-Zeile). Das eigene Pilotkonto braucht die
 * Antwort nicht -- es IST der Pilot.
 */
export function pilotIsDone(account: AdsOverviewAccount, pilot: AdsPilotInfo | null): boolean {
  if (account.is_pilot) return true;
  if (typeof account.pilot_done === 'boolean') return account.pilot_done;
  return pilot?.done ?? false;
}

/** Die Vorbedingungen, die jeder Schreibweg dieses Moduls teilt. */
function commonWriteBlock(
  account: AdsOverviewAccount | null,
  pilot: AdsPilotInfo | null,
): 'no_account' | 'negative_control' | 'write_disabled' | 'not_connected' | 'pilot_first'
  | 'experiment_running' | null {
  if (!account) return 'no_account';
  if (account.role === 'negative_control') return 'negative_control';
  if (!account.write_enabled) return 'write_disabled';
  if (!account.connected) return 'not_connected';
  if (!pilotIsDone(account, pilot)) return 'pilot_first';
  // Steht NACH der Pilotregel und VOR allem Lauf-Bezogenen: ein Experiment auf
  // einem Feed im Scope sperrt beide Schreibwege, und der Grund gilt für das
  // Konto, nicht für einen einzelnen Lauf. Die Übersicht meldet ihn seit dem
  // 23.08.2026 mit (Stufe 'overview'), die Oberfläche reicht ihn nur durch --
  // sie kennt `feed_experiments` nicht und soll es auch nicht.
  if (account.blocked_reason === 'experiment_running') return 'experiment_running';
  return null;
}

/**
 * Der Knopf "Übernehmen" braucht einen ANGEZEIGTEN Trockenlauf.
 *
 * `shownRun` ist deshalb der Lauf aus der Sitzung, nicht `latest_run` aus der
 * Übersicht: nach einem Neuladen der Seite ist er weg, und dann muss erst
 * wieder gerechnet und angesehen werden. Genau das verlangt die harte Grenze
 * ("ohne gesehenen und übernommenen Trockenlauf kein Schreibzugriff") --
 * ein Knopf, der einen ungesehenen Lauf übernimmt, wäre ein Blindflug.
 */
export function tierApplyBlock(
  account: AdsOverviewAccount | null,
  pilot: AdsPilotInfo | null,
  shownRun: AdsRunHead | null,
  now: number = Date.now(),
): TierApplyBlock | null {
  const common = commonWriteBlock(account, pilot);
  if (common) return common;
  if (!shownRun) return 'no_dry_run';
  if (shownRun.status !== 'dry_run') return 'run_already_applied';
  const age = runAgeHours(shownRun, now);
  if (age !== null && age >= DRY_RUN_MAX_AGE_HOURS) return 'dry_run_expired';
  return null;
}

/**
 * Der Label-Knopf eines FEEDS.
 *
 * Reihenfolge mit Absicht: erst die Kontogründe, dann G-D, dann was die
 * Function selbst am Konto meldet, zuletzt der Feed. Der erste Grund ist immer
 * der grundlegendste -- ein gesperrtes Konto soll nicht "kein freier Slot"
 * anzeigen.
 */
export function labelUploadBlock(
  account: AdsOverviewAccount | null,
  pilot: AdsPilotInfo | null,
  preview: AdsLabelPreview | null,
  feed: AdsLabelFeed | null,
): LabelUploadBlock | null {
  const common = commonWriteBlock(account, pilot);
  if (common) return common;
  // G-D, unabhängig davon, ob die Function es auch meldet: blind wird nie
  // rotiert, und diese Regel darf nicht daran hängen, dass die Antwort das
  // Feld trägt.
  if (account?.ingest_stale) return 'ingest_stale';
  // Zweite Hälfte derselben Regel, ebenfalls unabhängig von der Antwort der
  // Function. Ein Konto im Ausfall sieht an `ingest_stale` gesund aus.
  if (account?.data_blackout) return 'data_blackout';
  if (!preview) return 'no_preview';
  if (preview.account_blocked_reason) return preview.account_blocked_reason;
  if (!feed) return 'no_preview';
  if (feed.blocked_reason) return feed.blocked_reason;
  // Letzte eigene Prüfung: ein Lauf ohne labelbare Produkte ist kein Lauf.
  if (feed.eligible_with_tier <= 0) return 'nothing_eligible';
  return null;
}

// ═════════════════════════════════════════════════════════════════════════════
// Hooks -- eine je Aktion des Vertrags
// ═════════════════════════════════════════════════════════════════════════════

export const ADS_STEUERUNG_KEY = 'ads-steuerung';

const overviewKey = (userId: string | undefined) => [ADS_STEUERUNG_KEY, 'overview', userId ?? null];
const labelPreviewKey = (userId: string | undefined, customerId: string | null) =>
  [ADS_STEUERUNG_KEY, 'label-preview', userId ?? null, customerId];
// Das Limit gehört in den Schlüssel: seit die Label-Karte denselben Weg mit
// grösserer Tiefe abfragt, gibt es zwei Abfragen, die sich nur darin
// unterscheiden. Ohne das Limit im Schlüssel bekäme die tiefe Abfrage die
// 50 Zeilen der flachen serviert -- und liesse damit genau die Label-Läufe
// weg, wegen derer sie existiert. Die Invalidierung läuft über das Präfix
// `[ADS_STEUERUNG_KEY, 'change-log']` und trifft weiterhin beide.
const guardrailsKey = (userId: string | undefined, customerId: string | null) =>
  [ADS_STEUERUNG_KEY, 'guardrails', userId ?? null, customerId];
const changeLogKey = (userId: string | undefined, customerId: string | null, limit: number) =>
  [ADS_STEUERUNG_KEY, 'change-log', userId ?? null, customerId, limit];

/** `action: "overview"` -- die Rollout-Tafel. */
export function useAdsOverview() {
  const { user } = useAuth();
  return useQuery({
    queryKey: overviewKey(user?.id),
    enabled: !!user?.id,
    staleTime: 60_000,
    retry: adsQueryRetry,
    queryFn: async (): Promise<AdsOverview> =>
      normalizeOverview(await invokeAdsTierRun<unknown>({ action: 'overview' })),
  });
}

export interface DryRunVariables {
  ads_customer_id: string;
  window_days?: number;
  window_end?: string;
}

/**
 * `action: "dry_run"` -- rechnet und hält fest, schreibt aber nichts an die
 * Produkte. Bewusst eine Mutation und keine Query: der Lauf ist ein Vorgang mit
 * Zeitpunkt (er landet als Zeile in `ads_tier_runs`), kein Datenabruf, der sich
 * im Hintergrund wiederholen darf.
 */
export function useAdsDryRun() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<AdsDryRunResult, unknown, DryRunVariables>({
    mutationFn: async (variables): Promise<AdsDryRunResult> => {
      const raw = await invokeAdsTierRun<unknown>({ ...variables, action: 'dry_run' });
      const result = normalizeDryRun(raw);
      if (!result) throw new AdsSteuerungError('DRY_RUN_MALFORMED', 200);
      return result;
    },
    onSuccess: () => {
      // Der Lauf steht danach als `latest_run` in der Übersicht.
      queryClient.invalidateQueries({ queryKey: overviewKey(user?.id) });
    },
  });
}

/** `action: "apply"` -- schreibt das Tier in die Datenbank, nicht in den Feed. */
export function useAdsTierApply() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, { run_id: string }>({
    mutationFn: (variables) => invokeAdsTierRun<unknown>({ ...variables, action: 'apply' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overviewKey(user?.id) });
      // Ein übernommener Lauf hebt `no_applied_run` auf: die Vorschau muss neu.
      queryClient.invalidateQueries({ queryKey: [ADS_STEUERUNG_KEY, 'label-preview'] });
      queryClient.invalidateQueries({ queryKey: [ADS_STEUERUNG_KEY, 'change-log'] });
    },
  });
}

/**
 * `action: "rotation_pause"` -- der Veto-Schalter des Wochenlaufs. Pausiert
 * NUR die Upload-Phase (Montag 15:30); Rechnen und Übernehmen laufen weiter,
 * damit die Dämpfungskette nicht abreißt. Eine beim Pausieren stehende
 * Ankündigung wird von der Upload-Phase verworfen, nicht nachgeholt.
 */
export function useAdsRotationPause() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, { ads_customer_id: string; paused: boolean }>({
    mutationFn: (variables) =>
      invokeAdsTierRun<unknown>({ ...variables, action: 'rotation_pause' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overviewKey(user?.id) });
      queryClient.invalidateQueries({ queryKey: [ADS_STEUERUNG_KEY, 'change-log'] });
    },
  });
}

/**
 * `action: "label_preview"` -- was ein Label-Lauf je Feed täte.
 *
 * `options.enabled` ist die H6-F3-Bremse: fuer ein Konto ohne eigene
 * OAuth-Zeile (`connected: false` in der Uebersicht) antwortet die Function
 * deterministisch mit 403 ACCOUNT_NOT_AVAILABLE -- die Abfrage gar nicht erst
 * zu stellen ist die einzige Antwort, die keinen Fehlerbanner ueber den
 * designten Sperrzustand legt. Default true, damit sich am Vertrag fuer
 * andere Aufrufer nichts aendert.
 */
export function useAdsLabelPreview(
  customerId: string | null,
  options: { enabled?: boolean } = {},
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: labelPreviewKey(user?.id, customerId),
    enabled: !!user?.id && !!customerId && (options.enabled ?? true),
    staleTime: 60_000,
    retry: adsQueryRetry,
    queryFn: async (): Promise<AdsLabelPreview> =>
      normalizeLabelPreview(
        await invokeAdsTierRun<unknown>({
          action: 'label_preview',
          ads_customer_id: customerId,
        }),
      ),
  });
}

/**
 * Ergebnis eines Label-Laufs.
 *
 * `logged` ist der Grund, warum dieser Typ überhaupt ausgeschrieben ist: der
 * Upload läuft zu diesem Zeitpunkt bereits, und ein gescheiterter Eintrag ins
 * Änderungs-Log darf ihn weder als Fehlschlag darstellen noch stillschweigend
 * als Erfolg durchgehen. Das Log IST der Nachweis (Gültigkeitsrahmen
 * Abschnitt 3); eine Lücke darin fällt sonst erst bei der Auswertung Ende
 * Oktober auf, also dann, wenn sie nicht mehr zu schließen ist.
 */
export interface AdsLabelUploadResult {
  upload: unknown;
  logged: boolean;
  log_error: string | null;
}

function normalizeLabelUploadResult(raw: unknown): AdsLabelUploadResult {
  const record = isRecord(raw) ? raw : {};
  return {
    upload: record.upload ?? null,
    // Fehlt das Feld, gilt der Eintrag als NICHT geschrieben. Die vorsichtige
    // Annahme ist hier die richtige: ein zu Unrecht gemeldeter Protokollfehler
    // kostet einen Blick ins Log, ein übersehener kostet den Nachweis.
    logged: record.logged === true,
    log_error: typeof record.log_error === 'string' ? record.log_error : null,
  };
}

/** `action: "label_upload"` -- der einzige Weg, auf dem etwas zu Google geht. */
export function useAdsLabelUpload() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<AdsLabelUploadResult, unknown, { ads_customer_id: string; feed_id: string }>({
    mutationFn: async (variables) =>
      normalizeLabelUploadResult(
        await invokeAdsTierRun<unknown>({ ...variables, action: 'label_upload' }),
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: labelPreviewKey(user?.id, variables.ads_customer_id),
      });
      queryClient.invalidateQueries({ queryKey: [ADS_STEUERUNG_KEY, 'change-log'] });
    },
  });
}

/** `action: "change_log"` -- das Protokoll, optional auf ein Konto gefiltert. */
export function useAdsChangeLog(customerId: string | null = null, limit = 50) {
  const { user } = useAuth();
  return useQuery({
    queryKey: changeLogKey(user?.id, customerId, limit),
    enabled: !!user?.id,
    staleTime: 30_000,
    retry: adsQueryRetry,
    queryFn: async (): Promise<AdsChangeLogEntry[]> =>
      normalizeChangeLog(
        await invokeAdsTierRun<unknown>({
          limit,
          ...(customerId ? { ads_customer_id: customerId } : {}),
          // `action` steht zuletzt, ausnahmslos: kein Feld einer Variablen oder
          // eines bedingten Spreads darf die Modul-Aktion kippen können. Hier
          // trüge der Spread heute nie ein `action` -- aber genau so hat der
          // Fehler bei `change_log_add` angefangen.
          action: 'change_log',
        }),
      ),
  });
}

/**
 * `action: "guardrails"` -- der jüngste Befund je Konto und Guardrail.
 *
 * `staleTime` bewusst 5 Minuten und nicht 30 Sekunden wie das Änderungs-Log:
 * die Quelle ist ein TÄGLICHER Lauf. Häufiger zu fragen liefert dieselbe
 * Antwort und suggeriert eine Aktualität, die es nicht gibt.
 */
export function useAdsGuardrails(customerId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: guardrailsKey(user?.id, customerId),
    // Ohne gewaehltes Konto wird NICHT gefragt, genau wie bei der
    // Label-Vorschau. Die Tafel haengt am ausgewaehlten Konto; ohne diese
    // Bedingung liefe beim Laden erst eine kontoweite Abfrage und danach die
    // kontobezogene -- zwei Antworten fuer eine Flaeche, und die erste davon
    // fuer nichts. Der Waechter in AdsSteuerung.smoke.test.tsx zaehlt genau
    // deshalb die Lese-Aktionen mit.
    enabled: !!user?.id && !!customerId,
    staleTime: 300_000,
    retry: adsQueryRetry,
    queryFn: async (): Promise<AdsGuardrailReport> =>
      normalizeGuardrails(
        await invokeAdsTierRun<unknown>({
          ...(customerId ? { ads_customer_id: customerId } : {}),
          action: 'guardrails',
        }),
      ),
  });
}

/**
 * `action: "change_log_add"` -- ein Eintrag von Hand.
 *
 * `actor` wird hier NICHT mitgeschickt: die Function setzt ihn fest auf
 * `human`. Ihre eigenen Einträge schreibt die Software direkt, nie über
 * diesen Weg -- sonst wäre die Herkunft eines Eintrags eine Behauptung des
 * Aufrufers statt eine Eigenschaft des Wegs.
 */
export function useAdsChangeLogAdd() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, AdsChangeLogInput>({
    mutationFn: (variables) =>
      invokeAdsTierRun<unknown>({ ...variables, action: 'change_log_add' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ADS_STEUERUNG_KEY, 'change-log'] });
      // Ein von Hand nachgetragener `tier_apply` kann den Pilotstand ändern.
      queryClient.invalidateQueries({ queryKey: overviewKey(user?.id) });
    },
  });
}
